import React, { useState, useEffect } from "react";
import { TextField, Button, CircularProgress, Skeleton } from "@mui/material";
import { toast } from "react-toastify";

import { ApiService } from "../../ApiService";

import tiro_blanco from "../../../images/tiro_blanco.png";
import eye_vision from "../../../images/eye_vision.png";

export const VisitasMisionEdit = ({ companyData, currentBranch }) => {
	// Loading states
	const [isLoading, setIsLoading] = useState(false);
	const [isDataLoading, setIsDataLoading] = useState(true);

	// Card data
	const [cards, setCards] = useState([
		{
			img: tiro_blanco,
			title: "",
			titleOriginal: "",
			editingTitle: false,
			text: "",
			textOriginal: "",
			editingText: false,
			number: "01",
		},
		{
			img: eye_vision,
			title: "",
			titleOriginal: "",
			editingTitle: false,
			text: "",
			textOriginal: "",
			editingText: false,
			number: "02",
		},
	]);

	// Constants for character limits
	const TITLE_MAX = 50;
	const TEXT_MAX = 300;

	// Load company data on component mount or when companyData changes
	useEffect(() => {
		if (companyData) {
			setIsDataLoading(true);

			// Create a copy of cards to update
			const updatedCards = [...cards];
			// Update each card with data from companyData
			companyData.forEach((mission, index) => {
				if (index < updatedCards.length) {
					updatedCards[index].title = mission.title || "";
					updatedCards[index].titleOriginal = mission.title || "";
					updatedCards[index].text = mission.text || "";
					updatedCards[index].textOriginal = mission.text || "";
				}
			});

			setCards(updatedCards);
			setIsDataLoading(false);
		}
	}, [companyData]);

	// Title handlers
	const handleTitleChange = (e, index) => {
		if (e.target.value.length <= TITLE_MAX) {
			const updatedCards = [...cards];
			updatedCards[index].title = e.target.value;
			setCards(updatedCards);
		}
	};

	const activateTitleEdit = (index) => {
		const updatedCards = [...cards];
		// Disable editing for all fields
		updatedCards.forEach((card, i) => {
			card.editingTitle = false;
			card.editingText = false;
		});

		// Enable editing only for the selected title
		updatedCards[index].editingTitle = true;
		setCards(updatedCards);
	};

	const cancelTitleEdit = (index) => {
		const updatedCards = [...cards];
		updatedCards[index].title = updatedCards[index].titleOriginal;
		updatedCards[index].editingTitle = false;
		setCards(updatedCards);
	};

	const saveTitle = async (index) => {
		if (!currentBranch) return;

		const updatedCards = [...cards];
		const newTitle = updatedCards[index].title;

		if (!newTitle.trim()) {
			toast.error("El título no puede quedar vacío.");
			return;
		}

		setIsLoading(true);

		// Prepare the data for the API call - create an array with empty objects except for the updated one
		const updatedData = {
			view_mission: cards.map((_, i) => {
				if (i === index) {
					return {
						title: newTitle.trim(),
						text: cards[index].textOriginal, // Keep the original text value
					};
				}
				return {}; // Empty object for all other positions
			}),
		};

		try {
			const url = `api/company/update/us?key_branch=${currentBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify(updatedData),
			};

			const response = await ApiService.fetchData(url, payload);

			if (response?.code === 200) {
				// Update local state with new value
				updatedCards[index].titleOriginal = newTitle.trim();
				updatedCards[index].editingTitle = false;
				setCards(updatedCards);
				toast.success(response.message || "Título actualizado correctamente");
			} else {
				toast.error(response?.message || "Error al actualizar el título");
				// Revert to original value
				updatedCards[index].title = updatedCards[index].titleOriginal;
				setCards(updatedCards);
			}
		} catch (error) {
			console.error("Error updating title:", error);
			toast.error("Ocurrió un error al guardar el título");
			// Revert to original value
			updatedCards[index].title = updatedCards[index].titleOriginal;
			setCards(updatedCards);
		} finally {
			setIsLoading(false);
			updatedCards[index].editingTitle = false;
			setCards(updatedCards);
		}
	};
	// Text handlers
	const handleTextChange = (e, index) => {
		if (e.target.value.length <= TEXT_MAX) {
			const updatedCards = [...cards];
			updatedCards[index].text = e.target.value;
			setCards(updatedCards);
		}
	};

	const activateTextEdit = (index) => {
		const updatedCards = [...cards];

		// Disable editing for all fields
		updatedCards.forEach((card, i) => {
			card.editingTitle = false;
			card.editingText = false;
		});

		// Enable editing only for the selected text
		updatedCards[index].editingText = true;
		setCards(updatedCards);
	};

	const cancelTextEdit = (index) => {
		const updatedCards = [...cards];
		updatedCards[index].text = updatedCards[index].textOriginal;
		updatedCards[index].editingText = false;
		setCards(updatedCards);
	};

	const saveText = async (index) => {
		if (!currentBranch) return;

		const updatedCards = [...cards];
		const newText = updatedCards[index].text;

		if (!newText.trim()) {
			toast.error("El texto no puede quedar vacío.");
			return;
		}

		// setIsLoading(true);

		// Prepare the data for the API call
		const updatedData = {
			view_mission: cards.map((card, i) => {
				if (i === index) {
					return {
						title: card.title,
						text: newText.trim(),
					};
				}
				return {
					title: card.titleOriginal,
					text: card.textOriginal,
				};
			}),
		};

		try {
			const url = `api/company/update/us?key_branch=${currentBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify(updatedData),
			};

			const response = await ApiService.fetchData(url, payload);

			if (response?.code === 200) {
				// Update local state with new value
				updatedCards[index].textOriginal = newText.trim();
				updatedCards[index].editingText = false;
				setCards(updatedCards);
				toast.success(response.message || "Texto actualizado correctamente");
			} else {
				toast.error(response?.message || "Error al actualizar el texto");
				// Revert to original value
				updatedCards[index].text = updatedCards[index].textOriginal;
				setCards(updatedCards);
			}
		} catch (error) {
			console.error("Error updating text:", error);
			toast.error("Ocurrió un error al guardar el texto");
			// Revert to original value
			updatedCards[index].text = updatedCards[index].textOriginal;
			setCards(updatedCards);
		} finally {
			setIsLoading(false);
			updatedCards[index].editingText = false;
			setCards(updatedCards);
		}
	};
	return (
		<div className="mision-company">
			{/* TÍTULO PRINCIPAL (no editable) */}
			<div className="title-mision">
				<h2>Misión de la empresa</h2>
			</div>

			<div className="content-card-mision">
				{isDataLoading ? (
					<>
						<div className="card-mision-company">
							<Skeleton variant="rectangular" width="100%" height={300} />
						</div>
						<div className="card-mision-company">
							<Skeleton variant="rectangular" width="100%" height={300} />
						</div>
					</>
				) : (
					cards.map((card, index) => (
						<div className="card-mision-company" key={index}>
							<div className="article-mision">
								{/* IMG (no editable) */}
								<img src={card.img} alt={`Misión ${index + 1}`} />

								{/* TÍTULO (h3) → inline edit */}
								{card.editingTitle ? (
									<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
										<TextField
											variant="outlined"
											size="small"
											value={card.title}
											label={`Titulo: (${card.title.length} / 35 caracteres)`}
											slotProps={{
												htmlInput: {
													maxLength: 35,
												},
											}}
											onChange={(e) => handleTitleChange(e, index)}
											sx={{
												"& input": {
													textAlign: "center",
												},
											}}
										/>
										<div>
											<Button
												variant="contained"
												onClick={() => saveTitle(index)}
												disabled={isLoading}>
												{isLoading ? <CircularProgress size={20} /> : "Guardar"}
											</Button>
											<Button
												variant="outlined"
												onClick={() => cancelTitleEdit(index)}
												disabled={isLoading}
												style={{ marginLeft: 8 }}>
												Cancelar
											</Button>
										</div>
									</div>
								) : (
									<h3 style={{ cursor: "pointer" }} onClick={() => activateTitleEdit(index)}>
										{card.title || "Haz clic para editar el título"}
									</h3>
								)}

								{/* PÁRRAFO (p) → inline edit */}
								{card.editingText ? (
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "8px",
											zIndex: "3",
											width: "90%",
										}}>
										<TextField
											variant="outlined"
											size="small"
											multiline
											minRows={3}
											label={`Descripcion: (${card.text.length} / 235 caracteres)`}
											value={card.text}
											slotProps={{
												htmlInput: {
													maxLength: 235,
												},
											}}
											onChange={(e) => handleTextChange(e, index)}
											sx={{
												"& textarea": {
													textAlign: "left",
												},
											}}
										/>
										<div>
											<Button
												color="success"
												variant="contained"
												onClick={() => saveText(index)}
												disabled={isLoading}>
												{isLoading ? <CircularProgress size={20} /> : "Guardar"}
											</Button>
											<Button
												variant="outlined"
												onClick={() => cancelTextEdit(index)}
												disabled={isLoading}
												style={{ marginLeft: 8, backgroundColor: "#fff" }}>
												Cancelar
											</Button>
										</div>
									</div>
								) : (
									<p style={{ cursor: "pointer" }} onClick={() => activateTextEdit(index)}>
										{card.text || "Haz clic para editar el texto"}
									</p>
								)}
							</div>

							{/* Círculo con número (no editable) */}
							<div className="circle-number">
								<span>{card.number}</span>
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
};
